/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useMemo, useState } from "react";
import CadDownload from "~/features/productDetails/components/Cad/CadDownloadV2";
import TracePartsIframe from "~/features/productDetails/components/Cad/TracePartsIframe";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { Row } from "~/common/components/Layout";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { type CadTabProps } from "~/features/productDetails/components/PdpTabs/CadTab/CadTab";
import { fetchPartNumberAvailability } from "~/features/productDetails/components/Cad/api/fetchPartNumberAvailability";
import { CadForm } from "~/features/productDetails/components/Cad/CadTable/CadTable";
import {
  type CadFormDefinition,
  CadFormFieldType,
  type FieldDefinition,
  type FieldDefinitionDTO,
  transformCadFieldDefinition,
} from "~/features/productDetails/components/Cad/CadTypesV2";
import { isCADInquiryEnabled } from "~/utils/features";
import { useTranslation, type WithTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { fetchInitialConfiguration } from "~/features/productDetails/components/Cad/api/fetchInitialConfiguration";
import { fetchUpdateConfiguration } from "~/features/productDetails/components/Cad/api/fetchUpdateConfiguration";
import CadSurvey from "~/features/productDetails/components/Cad/CadSurvey";

const ConfiguredCadTab = ({ product }: CadTabProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectionPath, setSelectionPath] = useState("");
  const [partFamilyCode, setPartFamilyCode] = useState("");
  const [newValue, setNewValue] = useState<[string, string]>();
  const [dataTable, setDataTable] = useState<CadFormDefinition>([]);
  const [cadFormats, setCadFormats] = useState();

  const isDesktop = useIsMinDesktopSize();

  const { tracePartsUrl, tracePartsOrderNo, code, designation } = product;

  const cadProductDataUrl = useMemo(() => {
    if (!tracePartsUrl || !partFamilyCode || !selectionPath) {
      return "";
    }
    return `${tracePartsUrl}${partFamilyCode}&SelectionPath=${selectionPath}`;
  }, [tracePartsUrl, partFamilyCode, selectionPath]);

  useEffect(() => {
    (async () => {
      if (tracePartsOrderNo) {
        try {
          const data: any = await fetchPartNumberAvailability(tracePartsOrderNo);
          setPartFamilyCode(data.partFamilyCode);
          setSelectionPath(data.selectionPath);
          getInitialConfiguration(data.partFamilyCode, data.selectionPath);
        } catch {
          /* empty */
          setIsLoading(false);
        }
      }
    })();
  }, [tracePartsOrderNo]);

  const data = {
    selectionPath: selectionPath,
    partId: partFamilyCode,
    productDesignation: designation,
    productCode: code,
    cadFormats: cadFormats,
  };

  useEffect(() => {
    if (!(partFamilyCode && selectionPath && newValue)) {
      return;
    }
    const field = dataTable.find((field) => field.symbol === newValue[0]);
    if (field) {
      const value = field.editable ? "=" + newValue[1] : newValue[1];
      updateConfiguration(partFamilyCode, selectionPath, field.symbol, value);
    }
  }, [newValue]);

  const { t } = useTranslation();
  const { setValue } = useFormContext<AddToCartFormDraft>();
  useEffect(() => {
    if (dataTable?.length > 0 && isCADInquiryEnabled()) {
      const comment = generateAtcComment(dataTable, t);
      setValue("comment", comment);
    }
  }, [dataTable]);

  const getInitialConfiguration = async (partFamilyCode: string, selectionPath: string) => {
    setIsLoading(true);
    try {
      const data: any = await fetchInitialConfiguration({
        partId: partFamilyCode,
        selectionPath: selectionPath,
      });

      if (data?.configuratorList?.length > 0 && data?.globalInfo?.partFamilyInfo?.selectionPath) {
        const grid = data.configuratorList.findLast((configurator: any) => configurator.grid)?.grid;
        if (grid?.fieldGridList?.length > 0 && grid?.stepInfo?.currentStep) {
          const dataTable = (grid.fieldGridList as FieldDefinitionDTO[]).map(transformCadFieldDefinition);
          setDataTable(dataTable);
          setSelectionPath(data.globalInfo.partFamilyInfo.selectionPath);
        }
      }
      if (data?.cadFormatList?.length > 0) {
        setCadFormats(
          data.cadFormatList.map((format: { cadFormatId: string; cadFormatName: string }) => ({
            value: format.cadFormatId,
            label: format.cadFormatName,
          })),
        );
      }
    } catch {
      /* empty */
    }
    setIsLoading(false);
  };

  const updateConfiguration = async (partFamilyCode: string, selectionPath: string, symbol: string, value: string) => {
    setIsLoading(true);
    try {
      const data: any = await fetchUpdateConfiguration({
        partId: partFamilyCode,
        selectionPath: selectionPath,
        symbol: symbol,
        value: value,
      });

      if (data?.configuratorList?.length > 0 && data?.globalInfo?.partFamilyInfo?.selectionPath) {
        const grid = data.configuratorList.findLast((configurator: any) => configurator.grid)?.grid;
        if (grid?.fieldGridList?.length > 0 && grid?.stepInfo?.currentStep) {
          const dataTable = (grid.fieldGridList as FieldDefinitionDTO[]).map(transformCadFieldDefinition);
          setDataTable(dataTable);
          setSelectionPath(data.globalInfo.partFamilyInfo.selectionPath);
        }
      }
      if (data?.cadFormatList?.length > 0) {
        setCadFormats(
          data.cadFormatList.map((format: { cadFormatId: string; cadFormatName: string }) => ({
            value: format.cadFormatId,
            label: format.cadFormatName,
          })),
        );
      }
    } catch {
      /* empty */
    }
    setIsLoading(false);
  };

  return (
    <div className={"d-flex flex-direction-column" + (!isDesktop ? "container" : "")}>
      {isDesktop && (
        <Row>
          <div className="col-md-8">
            {isLoading && <CircleSpinner className={"mt-3"} />}
            {cadProductDataUrl && !isLoading && (
              <div className="scha__cad-iframe-container">
                <TracePartsIframe url={cadProductDataUrl} />
              </div>
            )}
          </div>

          <div className="col-md-4">
            {isLoading && <CircleSpinner className={"mt-3"} />}
            {cadProductDataUrl && !isLoading && cadFormats && (
              <>
                <CadDownload {...data} />
                <CadSurvey bearingDesignation={product.designation} />
              </>
            )}
          </div>
        </Row>
      )}
      {!isDesktop && (
        <div className={"scha__cad-iframe-download-tablet"}>
          <>
            {isLoading && <CircleSpinner className={"mt-3"} />}
            {cadProductDataUrl && !isLoading && cadFormats && (
              <>
                <CadDownload {...data} />
                <CadSurvey bearingDesignation={product.designation} />
              </>
            )}
          </>

          <div className="mt-60">
            {isLoading && <CircleSpinner className={"mt-3"} />}
            {cadProductDataUrl && !isLoading && (
              <div className="scha__cad-iframe-container">
                <TracePartsIframe url={cadProductDataUrl} />
              </div>
            )}
          </div>
        </div>
      )}
      {!isLoading && partFamilyCode !== "" && selectionPath !== "" && (
        <CadForm data={dataTable} onChange={setNewValue} />
      )}
    </div>
  );
};

export default memo(ConfiguredCadTab);

const generateAtcComment = (dataTable: FieldDefinition[], t: WithTranslation["t"]) => {
  let comment = t("page.pdp.cad.specification") + "\n";

  dataTable.forEach((field) => {
    let value;
    if (field.type == CadFormFieldType.Select) {
      value = field.valuesFieldGrid.find((ele) => ele.selected === "selected")?.label ?? "";
    } else {
      value = field.valuesFieldGrid[0]?.value ?? "";
    }
    comment += `${field.label}: ${value}\n`;
  });

  return comment;
};
